<template lang="pug">
.competitor-table
  table.competitor-row(v-if="results.length > 0")
    tbody
      tr
        th
          div.toggle-sort(@click="updateSort('rank', sortOrder === 'asc' ? 'desc' : 'asc')" :class="{active: sortMeasurementKey === 'rank'}") Rank
            Icon(:name="sortOrder === 'asc' ? 'arrow-down' : 'arrow-up'" fill="#000000" v-if="sortMeasurementKey === 'rank'")
        th
          .left-align Competitor
        th
        th
          div Gym
        th(v-if="showPointsColumn")
          div.toggle-sort(@click="updateSort('points', sortOrder === 'asc' ? 'desc' : 'asc')" :class="{active: sortMeasurementKey === 'points'}") Points
            Icon(:name="sortOrder === 'asc' ? 'arrow-down' : 'arrow-up'" fill="#000000" v-if="sortMeasurementKey === 'points'")
        th(v-for="name in eventNames")
          div.toggle-sort(@click="updateSort(name.eventName, sortOrder === 'asc' ? 'desc' : 'asc')" :class="{active: sortMeasurementKey === name.eventName}") {{name.headerName}}
            Icon(:name="sortOrder === 'asc' ? 'arrow-down' : 'arrow-up'" fill="#000000" v-if="sortMeasurementKey === name.eventName")
      tr.competitor(v-for="competitor in results")
        td.rank {{competitor.rank}}
        td.name
          span.name {{competitor.name}}
        td.flag
          StaticImage.flag-thumb(
            v-if="competitor.flag"
            :src="`cms/rogue%20challenge/flags/${updateFlagName(competitor.flag)}`"
            :alt="flagAlt(competitor.flag, $rogue)"
            :autoHeight="true"
            :width="32"
            :height="20"
          )
        td.gym {{competitor.gym || '-'}}
        td.points(v-if="showPointsColumn") {{competitor.points}}
        td.results(v-for="eventResult in formatCompetitorResults(competitor)")
          span.results-container
            span {{eventResult.formattedResult}}
            span(v-if="selectedEvent.showPoints && eventResult.points") &nbsp;({{ eventResult.points }})
            span.video(v-if="eventResult.video && selectedEvent.isSeasonChallenge && DISPLAYED_VIDEO_RANKS.includes(competitor.rank)" )
              StaticImage.video-icon(@click="handleVideoModal(eventResult)" :alt="__('Watch video')" src="cms/rogue%20challenge/yt_icon_mono_light_q79cz0.png" :autoHeight="true" :width="20")
  .no-results(v-else) {{ __('No Results') }}
  Modal(v-if="showVideoModal" width="1280px" @close="closeModal")
    .modal-content
      .youtube.inline-video(v-if="videoUrl.indexOf('you') > -1")
        iframe(
          :src="getYoutubeUrl(videoUrl)"
          frameBorder="0"
          class="inline-iframe"
          title="youtube-review"
          allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        )
      .vimeo.inline-video(v-else-if="videoUrl.indexOf('vim') > -1")
        iframe(
          class="inline-iframe"
          title="vimeo-player"
          :src="getVimeoUrl(videoUrl)"
          frameBorder="0"
          allowFullScreen
        )
      .error(v-else)
        .whoops {{ __('Video url does not exist') }}
</template>
<script setup lang="ts">
import { flagAlt } from 'sisu/js/helpers'
import { ref, computed, type PropType } from 'vue'
import type { IEvent, IDivision, CompetitorResultInfo, EventResult } from './leaderboard-types'
import { buildTime, updateFlagName } from './utils'

// Showing the video icon only for the top 3 ranks and ties
const DISPLAYED_VIDEO_RANKS = ["1", "2", "3", "1T", "2T", "3T"]

const props = defineProps({
  selectedEvent: {
    type     : Object as PropType<IEvent>,
    required : true,
  },
  selectedDivision: {
    type     : Object as PropType<IDivision>,
    required : true,
  },
  results: {
    type    : Array,
    default : () => ([]),
  },
  events: {
    type    : Array as PropType<IEvent[]>,
    default : () => ([]),
  },
  name: {
    type    : String,
    default : '',
  },
  sortOrder: {
    type    : String,
    default : 'asc',
  },
  sortMeasurementKey: {
    type    : String,
    default : '',
  },
  showResults: {
    type    : Boolean,
    default : false,
  },
})

const showVideoModal = ref(false)
const videoUrl = ref<string | undefined>('')

const emit = defineEmits(['updateSort'])

const eventNames = computed(() => props.selectedEvent.scoringColumns?.map(key => ({ headerName: key.name, eventName: key.event })))

const showPointsColumn = computed<boolean>(() => (props.selectedEvent.season && +props.selectedEvent.season < 2026) || !!props.selectedEvent.isSeasonChallenge || !!props.selectedEvent.showPoints)

function updateSort(sortMeasurementKey: string, sortOrder: string) {
  emit('updateSort', { sortMeasurementKey, sortOrder })
}

function formatResult(eventResult: EventResult) {
  const unit = eventResult?.unit
  const value = eventResult?.value
  const video = eventResult?.video
  const points = eventResult?.points

  if (!value) {
    return { formattedResult: '-' }
  }

  let formattedResult = `${value} ${unit || ''}`.trim()

  if (unit === 'seconds' || unit === 's' || unit === 'time') {
    formattedResult = buildTime(value)
  } else if (unit === 'msec' || unit === 'msecs') {
    formattedResult = buildTime(value / 1000)
  }

  return { formattedResult, video, points }
}

function formatCompetitorResults(competitor: CompetitorResultInfo) {
  const results = competitor.results

  return props.selectedEvent.scoringColumns?.length && results !== undefined ? props.selectedEvent.scoringColumns.map(column => formatResult(results[column.event])) : []
}

function getYoutubeUrl(string: string) {
  const regex = /.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=)([^#]*).*/
  const match = string.match(regex)
  if (match && match[1].length) return `https://www.youtube.com/embed/${match[1]}`

  return '-'
}

function getVimeoUrl(string: string) {
  const id = string.split('/').slice(-1)[0].split('?')[0]

  return `https://player.vimeo.com/video/${id}`
}

function handleVideoModal(eventResult: EventResult) {
  const videoResult = eventResult.video

  videoUrl.value = videoResult
  showVideoModal.value = true
}

function closeModal() {
  showVideoModal.value = false
  videoUrl.value = ''
}
</script>

<style lang="scss" scoped>
.competitor-table {
  overflow-x: scroll;
  background-color: $color-white;
}

table.competitor-row {
  border: none;
  max-width: 100%;
}

table.competitor-row tr th {
  border-bottom: 1px solid $color-black;
  text-transform: uppercase;
  font-size: $size-s;
  padding: 0;
}

table.competitor-row tr th div {
  padding: $size-xxs;
}

table.competitor-row tr th .left-align {
  text-align: left;
}

table.competitor-row tr td {
  border-bottom: 1px solid $color-gray-light;
  font-size: $size-xs;
}

table.competitor-row .toggle-sort {
  cursor: pointer;
  display: flex;
  flex-direction: row;
  justify-content: center;
  white-space: nowrap;
}

table.competitor-row th .toggle-sort.active {
  background: $color-gray-light;
}

table.competitor-row th .toggle-sort.active .icon {
  height: $size-xxs;
  width: $size-xxs;
  margin-left: 6px;
  margin-top: 3px;
}

table.competitor-row td:first-child {
  z-index: 1;
}

.competitor .name {
  position: relative;
  text-align: left;
  min-width: 150px;
}

.competitor td.name {
  padding: 0px 12px;
}

.competitor td.name span.name {
  margin-right: $size-l;
  padding: $space-xxs 0;
}

.competitor .results-container {
  position: relative;
}

.competitor .results-container .video {
  position: absolute;
  right: $size-xxs;
  width: $size-l;
  top: -2px;
  right: -30px;
}

.modal-content .inline-video {
  position: relative;
  width: 100%;
  height: 0;
  padding-top: 56.25%;
}

.modal-content .inline-video iframe {
  width: 100%;
  position: absolute;
  height: 100%;
  top: 0;
  left: 0;
}

.flag-thumb {
  width: $space-l;
  max-height: $space-s;
}

.no-results {
  margin: 40px;
  text-align: center;
}
</style>
